import { RemoveCircle } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import useOrderWorkunitsService from 'features/Orders/OrderWorkunits/services/useOrderWorkunitsService';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'store/hooks';
import { useGridApiContext } from '@mui/x-data-grid-pro';
import { OrderStatus } from 'types/enums/OrderStatus';

const ContentHeader = ({ number, type }: { number: number; type: 'catalog' | 'order' }) => {
  const [t] = useTranslation();
  const contentColumnsNumber = useSelector((state) =>
    type === 'catalog'
      ? state.catalogToOrder.catalogHandler.contentsNumber
      : state.catalogToOrder.orderHandler.contentsNumber
  );
  const isProductionOrder = useSelector((state) => state.catalogToOrder.order?.status === OrderStatus.PRODUCTION);
  // AddCircleIcon/RemoveCircle pass an explicit `color` prop, so they don't inherit the
  // IconButton's disabled dimming via currentColor - grey them out manually whenever the
  // button is disabled, for both the catalog and "My order" grids.
  const shouldGreyOut = isProductionOrder;

  const apiRef = useGridApiContext();

  const activeEditRows = useSelector((state) => state.catalogToOrder.selectedWorkunitsEditIds);

  const {
    increaseCatalogContentColumnsNumber,
    decreaseCatalogContentColumnsNumber,
    increaseOrderContentColumnsNumber,
    decreaseOrderContentColumnsNumber,
  } = useOrderWorkunitsService();

  const handleDecrease = async () => {
    const contentField = `content${number > 0 ? number : ''}`;
    for (let i = 0; i < activeEditRows.length; i += 1) {
      if (apiRef.current.getCellMode(activeEditRows[i], contentField) === 'edit') {
        apiRef.current.stopCellEditMode({ id: activeEditRows[i], field: contentField });
      }
    }

    /* Trick to wait for cells edit mode to be stopped before decreasing 
      the number of contents columns in order to avoid Datagrid's undefined valueSetter error */
    await new Promise((r) => setTimeout(r, 0));
    /* ---- */

    if (type === 'catalog') {
      decreaseCatalogContentColumnsNumber();
    } else {
      decreaseOrderContentColumnsNumber();
    }
  };

  const handleIncrease = () => {
    if (type === 'catalog') {
      increaseCatalogContentColumnsNumber();
    } else {
      increaseOrderContentColumnsNumber();
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {number > 1 && number === contentColumnsNumber && (
        <IconButton size="small" onClick={handleDecrease} disabled={isProductionOrder}>
          <RemoveCircle fontSize="inherit" color={shouldGreyOut ? 'disabled' : 'error'} />
        </IconButton>
      )}
      <Typography variant="body2">
        {t('Content')} {number === 1 ? '' : number}
      </Typography>
      {number < 5 && number === contentColumnsNumber && (
        <IconButton size="small" onClick={handleIncrease} disabled={isProductionOrder}>
          <AddCircleIcon fontSize="inherit" color={shouldGreyOut ? 'disabled' : 'primary'} />
        </IconButton>
      )}
    </Box>
  );
};

export default memo(ContentHeader);
